import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Plus, Download, RefreshCw } from 'lucide-react';

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadDashboard = useCallback(async (mobileNumber) => {
    try {
      const response = await fetch(`${API_URL}/api/parent/dashboard/${encodeURIComponent(mobileNumber)}`);
      const data = await response.json();

      if (data.success) {
        setDashboard(data);
      } else {
        setError('Failed to load dashboard');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    const mobileNumber = localStorage.getItem('parent_mobile');
    
    if (!mobileNumber) {
      navigate('/parent-login');
      return;
    }

    loadDashboard(mobileNumber);
  }, [navigate, loadDashboard]);

  const handleLogout = () => {
    localStorage.removeItem('parent_mobile');
    navigate('/parent-login');
  };

  const handleAddChild = () => {
    // Store flag to return to dashboard after questionnaire
    localStorage.setItem('return_to_dashboard', 'true');
    navigate('/questionnaire');
  };

  const handleViewReport = (reportId) => {
    navigate(`/parent-report/${reportId}`);
  };

  const handleRerun = (child) => {
    // Store child data for re-run
    localStorage.setItem('rerun_child_id', child.child_id);
    localStorage.setItem('rerun_child_name', child.child_name);
    navigate('/questionnaire');
  };

  const handleUpgrade = async (child) => {
    // Navigate to upgrade flow
    navigate(`/upgrade-report/${child.child_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                My Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {dashboard?.total_children || 0} {dashboard?.total_children === 1 ? 'child' : 'children'} · Family-centered recommendations
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Another Child CTA */}
        <button
          onClick={handleAddChild}
          className="w-full sm:w-auto mb-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg inline-flex items-center justify-center gap-2 transition shadow-md hover:shadow-lg"
        >
          <Plus size={20} />
          Add Another Child
        </button>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Children Cards */}
        {dashboard?.total_children === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to Switch Schools!
              </h2>
              <p className="text-gray-600 mb-6">
                Start by running your first school recommendation for your child.
              </p>
              <button
                onClick={handleAddChild}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg inline-flex items-center gap-2 transition"
              >
                Get Started
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboard?.children.map((child) => (
              <div key={child.child_id} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all p-6 border border-gray-100">
                {/* Child Name & Grade */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {child.child_name}
                  </h3>
                  {child.has_report && (
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        child.report_type === 'Premium' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {child.report_type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(child.latest_report_date).toLocaleDateString('en-MY', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {child.has_report && (
                    <>
                      <button
                        onClick={() => handleViewReport(child.report_id)}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-2.5 px-4 rounded-lg transition text-sm"
                      >
                        View Report
                      </button>

                      {/* Re-run */}
                      <button
                        onClick={() => handleRerun(child)}
                        className="w-full bg-white hover:bg-gray-50 text-gray-900 font-medium py-2.5 px-4 rounded-lg border-2 border-gray-200 transition text-sm flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Re-run Recommendation
                        <span className="ml-auto font-bold text-blue-600">
                          RM {child.rerun_price?.toFixed(2)}
                        </span>
                      </button>
                      
                      {child.report_type === 'Premium' && (
                        <p className="text-xs text-gray-500 text-center">
                          Returning Premium User Price
                          <span className="line-through ml-1">RM 14.99</span>
                        </p>
                      )}

                      {/* Upgrade Option (only for Basic) */}
                      {child.can_upgrade && (
                        <>
                          <button
                            onClick={() => handleUpgrade(child)}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition text-sm flex items-center justify-center gap-2"
                          >
                            Upgrade to Premium
                            <span className="ml-auto font-bold">
                              +RM {child.upgrade_price?.toFixed(2)}
                            </span>
                          </button>
                          <p className="text-xs text-gray-500 text-center">
                            Unlock 3 schools + 3 near misses
                          </p>
                        </>
                      )}
                    </>
                  )}

                  {!child.has_report && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No report yet</p>
                    </div>
                  )}
                </div>

                {/* Download PDF (Future) */}
                {child.has_report && (
                  <button
                    className="w-full mt-3 text-blue-600 hover:text-blue-700 font-medium py-2 text-sm flex items-center justify-center gap-2"
                  >
                    <Download size={16} />
                    Download PDF
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Help Text */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>Need help? Email us at support@switchschools.my</p>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
